import api, { V2 } from './axios';

export type PaymentStatus =
  | 'pending'
  | 'awaiting_verification'
  | 'success'
  | 'failed'
  | 'refunded';

export interface InitiatedPayment {
  payment_id: string;
  final_amount: number;
  status: PaymentStatus;
  provider: 'cashfree' | 'manual_upi';
  /** Present only when the gateway is on — hand it to the Cashfree SDK. */
  payment_session_id?: string;
  /** Which Cashfree environment the order was created in. */
  gateway_env?: 'sandbox' | 'production';
  order_id?: string;
  cf_order_id?: string;
  reused?: boolean;
}

/** A coupon as the list endpoint returns it. Empty `applicable_services`
 *  means it works on every service, consultations included. */
export interface Coupon {
  code: string;
  description?: string;
  discount_type?: string;
  discount_value?: number;
  min_order?: number;
  max_discount?: number;
  applicable_services?: string[];
}

export const paymentsApi = {
  initiate: (data: Record<string, unknown>) =>
    api.post(`${V2}payments/initiate/`, data),

  confirm: (data: Record<string, unknown>) =>
    api.post(`${V2}payments/confirm/`, data),

  /** Server-verified status. Safe to poll: each call re-checks the gateway. */
  getStatus: (paymentId: string) =>
    api.get<{ payment_id: string; status: PaymentStatus }>(
      `${V2}payments/status/${encodeURIComponent(paymentId)}/`,
    ),

  /** Shopper closed checkout without paying — releases the slot if the
   *  gateway confirms no attempt is still in flight. */
  abandon: (paymentId: string) =>
    api.post<{ status: PaymentStatus; released: boolean }>(
      `${V2}payments/abandon/`, { payment_id: paymentId }),

  cancel: (data: { payment_id: string }) =>
    api.post(`${V2}payments/cancel/`, data),

  getHistory: () =>
    api.get(`${V2}payments/history/`),

  getDetail: (paymentId: string) =>
    api.get(`${V2}payments/detail/${encodeURIComponent(paymentId)}/`),

  /** `service_slug` scopes the list to coupons valid for that service. */
  getCoupons: (params?: { service_slug?: string }) =>
    api.get(`${V2}payments/coupons/`, { params }),

  validateCoupon: (data: {
    code: string;
    service_id?: string;
    service_slug?: string;
    amount?: number;
  }) => api.post(`${V2}payments/coupons/validate/`, data),
};
